
import React from 'react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import DashboardSidebar from './DashboardSidebar';
import { ProfileData } from '@/components/profile/ProfileContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
  isLoading?: boolean;
  profileData?: ProfileData | null;
  hasProfileData?: boolean;
  isProfileComplete?: boolean;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ 
  children, 
  isLoading,
  profileData = null,
  hasProfileData = false,
  isProfileComplete = false
}) => {
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-t-2 border-[#9E7C47] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-black text-white flex w-full">
        {/* Sidebar */}
        <DashboardSidebar 
          profileData={profileData}
          hasProfileData={hasProfileData}
          isProfileComplete={isProfileComplete}
        />
        
        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile header with menu trigger */}
          <div className="md:hidden flex items-center justify-between p-4 border-b border-gray-700 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/60">
            <SidebarTrigger>
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle sidebar</span>
            </SidebarTrigger>
            
            <div className="text-lg font-semibold bg-gradient-to-r from-[#9E7C47] to-[#F9F1A9] bg-clip-text text-transparent">
              Wealth Manager
            </div>
            
            <div className="w-9" /> {/* Spacer for centering */}
          </div>

          {/* Desktop header with trigger (hidden on mobile) */}
          <div className="hidden md:flex items-center justify-between p-6 border-b border-gray-700 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/60">
            <div className="flex items-center gap-4">
              <SidebarTrigger>
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle sidebar</span>
              </SidebarTrigger>
              
              <div className="text-xl font-bold bg-gradient-to-r from-[#9E7C47] to-[#F9F1A9] bg-clip-text text-transparent">
                Dashboard
              </div>
            </div>
          </div>

          {/* Main content */}
          <main className="flex-1 overflow-auto">
            <div className="container mx-auto p-4 md:p-6 max-w-7xl">
              {children}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default DashboardLayout;
